import { registerMutation, registerQuery } from 'data-fetching-client/next';
const QueryMap = Map;
const createQueryCache = () => ({
  mutations: new QueryMap(),
  queries: new QueryMap()
});
export const configQueryCache = () => {
  // 1. Create a typed QUERY_CACHE
  const CACHE = createQueryCache();

  // 2. Create typed versions of:
  // `getAnyCachedQuery`, `getCachedMutation`, `getCachedQuery`,
  // `registerMutationToCache`, `registerQueryToCache`, `cacheMutation`, `cacheQuery`
  const getAnyCachedQuery = fieldName => {
    const mutation = CACHE.mutations.get(fieldName);
    const query = CACHE.queries.get(fieldName);
    if (!mutation && !query) {
      throw new Error(`Attempted to get a mutation or query that has not been cached (name: ${fieldName})`);
    }
    return mutation || query;
  };
  const getCachedMutation = fieldName => CACHE.mutations.get(fieldName);
  const getCachedQuery = fieldName => CACHE.queries.get(fieldName);
  const assertMutationIsNotAQuery = fieldName => {
    try {
      const query = getCachedQuery(fieldName);
      if (query) {
        throw new Error(`Attempted to cache a mutation that was already cached as a query (name: ${fieldName})`);
      }
    } catch (_unused) {
      return;
    }
  };
  const assertQueryIsNotAMutation = fieldName => {
    try {
      const mutation = getCachedMutation(fieldName);
      if (mutation) {
        throw new Error(`Attempted to cache query that was already cached as a mutation (name: ${fieldName})`);
      }
    } catch (_unused2) {
      return;
    }
  };
  const registerMutationToCache = options => {
    const {
      fieldName
    } = options;
    assertMutationIsNotAQuery(fieldName);
    let mutation = getCachedMutation(fieldName);
    if (!mutation) {
      mutation = registerMutation(options);
      CACHE.mutations.set(fieldName, mutation);
    }
    return mutation;
  };
  const registerQueryToCache = options => {
    const {
      fieldName
    } = options;
    assertQueryIsNotAMutation(fieldName);
    let query = getCachedQuery(fieldName);
    if (!query) {
      query = registerQuery(options);
      CACHE.queries.set(fieldName, query);
    }
    return query;
  };
  const cacheMutation = (fieldName, query) => {
    assertMutationIsNotAQuery(fieldName);
    try {
      const existingMutation = getCachedMutation(fieldName);
      if (existingMutation !== query) {
        throw new Error(`Attempted to register a mutation that was already registered (name: ${fieldName})`);
      }
    } catch (_unused3) {
      CACHE.mutations.set(fieldName, query);
    }

    // this is just to keep the APIs consistent, the consumer would already have access to the query
    return query;
  };
  const cacheQuery = (fieldName, query) => {
    assertQueryIsNotAMutation(fieldName);
    try {
      const existingQuery = getCachedQuery(fieldName);
      if (existingQuery !== query) {
        throw new Error(`Attempted to register a query that was already registered (name: ${fieldName})`);
      }
    } catch (_unused4) {
      CACHE.queries.set(fieldName, query);
    }

    // this is just to keep the APIs consistent, the consumer would already have access to the query
    return query;
  };
  const registerApolloQueryToCache = options => {
    const {
      fieldName,
      query: gqlQuery
    } = options;
    assertQueryIsNotAMutation(fieldName);
    const query = getCachedQuery(fieldName);
    if (!query) {
      CACHE.queries.set(fieldName, gqlQuery);
    }
    return query;
  };
  return {
    /**
     * @description Caches a mutation that was registered without using
     * {@link registerMutationToCache}. This function can be used to incorporate auto-generated
     * hooks, such as those from CHIRP.
     */
    cacheMutation,
    /**
     * @description Caches a query that was registered without using {@link registerQueryToCache}.
     * This function can be used to incorporate auto-generated hooks, such as those from CHIRP.
     */
    cacheQuery,
    /**
     * @description Grabs a registered query or mutation by the provided field name
     * @throws If no query or mutation can be found for the provided `fieldName`
     */
    getAnyCachedQuery,
    /**
     * @description Grabs a mutation by its known data key.
     * @throws If no mutation can be found for the provided key, an error is thrown.
     */
    getCachedMutation,
    /**
     * @description Grabs a query by its known data key.
     * @throws If no mutation can be found for the provided key, an error is thrown.
     */
    getCachedQuery,
    /**
     * @description Registers a mutation for a known data type. If a mutation has already been
     * registered with a `fieldName`, it will return the first-registered mutation.
     */
    registerMutationToCache,
    /**
     * @description Registers a query for a known data type. If a query has already been registered
     * with a `fieldName`, it will return the first-registered query.
     */
    registerQueryToCache,
    /**
     * @description Registers an Apollo query for a known data type. If a query has already been
     * registered with a `fieldName`, it will return the first-registered query.
     */
    registerApolloQueryToCache
  };
};